import React, { FC } from 'react';
import { Moment } from 'moment';
import Button from 'components/Button/Button';
import { useUserTimezone } from 'hooks/useGetDateWithTimeZone';
import { getNowInTimezone } from 'helpers/timezoneUtils';
import { SDatePicker } from './DatePicker.style';
import { TDatePickerProps } from './DatePicker.type';

type TTimezoneDatePickerProps = TDatePickerProps & {
  value?: Moment | null;
  onChange?: (date: Moment | null) => void;
};

/**
 * A DatePicker for timezone-aware time entry.
 *
 * How it works:
 * - The user selects a time thinking it's in their profile's selected timezone
 * - The time is passed as-is to the backend
 * - The backend interprets the time using the user's timezone from their profile
 *
 * Note: No frontend conversion is needed because the backend handles timezone
 * interpretation based on the user's profile timezone setting (ZEST-579).
 *
 * ZEST-582: Custom "Now" button that uses the user's profile timezone instead of browser time
 */
const TimezoneDatePicker: FC<TTimezoneDatePickerProps> = ({
  value,
  onChange,
  ...props
}) => {
  const { timeZone, abbreviation } = useUserTimezone();

  const handleNowClick = () => {
    const nowInUserTimezone = getNowInTimezone(timeZone);
    if (onChange) {
      onChange(nowInUserTimezone);
    }
  };

  const renderExtraFooter = () => (
    <Button
      type="link"
      size="small"
      onClick={handleNowClick}
      style={{ padding: '0 8px' }}
    >
      Now ({abbreviation})
    </Button>
  );

  const datePickerProps = {
    ...props,
    showNow: false,
    renderExtraFooter,
    value,
    onChange,
  };

  return (
    <SDatePicker
      data-testid="timezone-date-picker"
      {...(datePickerProps as TDatePickerProps)}
    />
  );
};

export default TimezoneDatePicker;
